import React, { useMemo } from "react";
import { Editable, withReact } from "slate-react";
import { Element as SlateElement, Editor, Transforms, createEditor, Range } from "slate";
import isUrl from "is-url";
import { isKeyHotkey } from "is-hotkey";
import * as SlateReact from "slate-react";
import Link from "./Link";
import Text from "./Text";
import CustomEditor, { toggleBlock } from "./CustomEditor";

function RichTextEditor({initialValue, setValue}) {
    const editor = useMemo(() => withInlines(withReact(createEditor())), []);

    const onKeyDown = event => {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
            const { nativeEvent } = event;
            if (isKeyHotkey("left", nativeEvent)) {
                event.preventDefault();
                Transforms.move(editor, { unit: "offset", reverse: true });
                return
            }
            if (isKeyHotkey("right", nativeEvent)) {
                event.preventDefault();
                Transforms.move(editor, { unit: "offset" });
                return
            }
        }
    }

    const onChange = value => {
        const isAstChange = editor.operations.some(op => "set_selection" !== op.type);
        if (isAstChange) {
            setValue(value);
            const content = JSON.stringify(value);
            localStorage.setItem("content", content);
        }
    }

    return (
        <SlateReact.Slate editor={editor} initialValue={initialValue} onChange={onChange}>
            <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-100 border border-gray-300 rounded-t-lg">
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault()
                        const url = window.prompt("Enter the URL of the link:")
                        if (!url) return
                        insertLink(editor, url)
                    }}
                >
                    Add Link
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        if (isLinkActive(editor)) {
                            unwrapLink(editor)
                        }
                    }}
                >
                    Remove Link
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        toggleBlock(editor, "h1");
                    }}
                >
                    H1
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        toggleBlock(editor, "h2");
                    }}
                >
                    H2
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        toggleBlock(editor, "h3");
                    }}
                >
                    H3
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        CustomEditor.toggleMark(editor, "bold");
                    }}
                >
                    Bold
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        CustomEditor.toggleMark(editor, "italic");
                    }}
                >
                    Italic
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        CustomEditor.toggleMark(editor, "strikethrough");
                    }}
                >
                    Strikethrough
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        toggleBlock(editor, "bulleted-list");
                    }}
                >
                    Bulleted List
                </button>
                <button
                    type="button"
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    onMouseDown={event => {
                        event.preventDefault();
                        toggleBlock(editor, "numbered-list");
                    }}
                >
                    Numbered List
                </button>
            </div>
            <Editable
                renderElement={props => <Element {...props} />}
                renderLeaf={props => <Text {...props} />}
                placeholder="Enter some text..."
                onKeyDown={onKeyDown}
                className="p-4 bg-white border border-gray-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
        </SlateReact.Slate>
    )
}

const Element = props => {
    const { attributes, children, element } = props;

    switch (element.type) {
        case "h1":
            return (
                <h1 className="text-4xl font-black" {...props.attributes}>
                    {props.children}
                </h1>
            );
        case "h2":
            return (
                <h2 className="text-3xl font-extrabold" {...props.attributes}>
                    {props.children}
                </h2>
            );
        case "h3":
            return (
                <h3 className="text-2xl font-extrabold" {...props.attributes}>
                    {props.children}
                </h3>
            );
        case "list-item":
            return (
                <li {...props.attributes}>
                    {props.children}
                </li>
            );
        case "bulleted-list":
            return (
                <ul className="list-disc list-inside space-y-2" {...props.attributes}>
                    {props.children}
                </ul>
            );
        case "numbered-list":
            return (
                <ol className="list-decimal list-inside space-y-2" {...props.attributes}>
                    {props.children}
                </ol>
            );
        case "link":
            return <Link {...props} />
        default:
            return <p {...attributes}>{children}</p>
    }
}

const withInlines = editor => {
    const { insertData, insertText, isInline, isElementReadOnly, isSelectable } = editor;
    editor.isInline = element => ["link"].includes(element.type) || isInline(element);
    editor.isElementReadOnly = element => isElementReadOnly(element);
    editor.isSelectable = element => isSelectable(element);
    editor.insertText = text => {
        if (text && isUrl(text)) {
            wrapLink(editor, text)
        } else {
            insertText(text)
        }
    }
    editor.insertData = data => {
        const text = data.getData("text/plain")
        if (text && isUrl(text)) {
            wrapLink(editor, text)
        } else {
            insertData(data)
        }
    }
    return editor
}

const isLinkActive = editor => {
    const [link] = Editor.nodes(editor, {
        match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
    })
    return !!link
}

const insertLink = (editor, url) => {
    if (editor.selection) {
        wrapLink(editor, url)
    }
}

const unwrapLink = editor => {
    Transforms.unwrapNodes(editor, {
        match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === "link",
    })
}

const wrapLink = (editor, url) => {
    if (isLinkActive(editor)) {
        unwrapLink(editor);
    }

    const { selection } = editor;
    const isCollapsed = selection && Range.isCollapsed(selection);
    const link = {
        type: "link",
        url,
        children: isCollapsed ? [{ text: url }] : [],
    };

    if (isCollapsed) {
        Transforms.insertNodes(editor, link)
    } else {
        Transforms.wrapNodes(editor, link, { split: true });
        Transforms.collapse(editor, { edge: "end" });
    }
}

export default RichTextEditor;